// API response types — matches api/response_transform.py output exactly.
// Drift between this file and the Python transform is a contract bug.

export type DirectionalLabel =
  | "strong_buyer_advantage"
  | "moderate_buyer_advantage"
  | "neutral"
  | "moderate_seller_advantage"
  | "strong_seller_advantage";

export type ConfidenceLabel =
  | "high"
  | "moderate"
  | "reduced — some data unavailable";

export interface OfferRange {
  low_pct: number;
  high_pct: number;
  low_price: number;
  high_price: number;
}

export interface FactorCard {
  name: string;
  direction: DirectionalLabel;
  direction_label: string;
  facts: string[];
  summary: string;
}

export interface FlipRisk {
  triggered: true;
  risk_score: number;
  risk_band: "low" | "mid" | "high";
  risk_band_label: string;
  prior_sale_date: string; // ISO 8601
  prior_sale_price: number;
  markup_pct: number;
  holding_period_years: number;
  permits_during_hold: Array<Record<string, unknown>>;
  inspector_priorities: string[];
  offer_language: string;
}

export interface ClosingCost {
  monthly_insurance_low: number | null;
  monthly_insurance_high: number | null;
  monthly_tax_forecast: number | null;
  monthly_tax_case: "A" | "B";
  monthly_tax_disclosure: string;
  monthly_hoa: number;
  monthly_total_midpoint: number | null;
  volatility_flag: boolean;
  volatility_message: string | null;
  disclaimer: string;
}

export interface MortgageRate {
  rate: number;
  as_of: string | null;
  source: string;
}

export interface ChatTurn {
  turn_id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string; // ISO 8601
}

/** Walk Score fact (display only). Carries the attribution the license
 * requires: `logo_url` + a `link` back to Walk Score. transit/bike are
 * coverage-dependent and may be absent. */
export interface WalkScoreFact {
  walkscore: number;
  description: string | null;
  link: string | null;
  logo_url: string | null;
  transit?: { score: number; description: string | null };
  bike?: { score: number; description: string | null };
}

/** HowLoud Soundscore fact (display only). `score` is HIGHER = QUIETER; the
 * traffic/airports/local components each have a value + `*text` label. */
export interface SoundscoreFact {
  score: number;
  scoretext: string | null;
  traffic?: number;
  traffictext?: string | null;
  airports?: number;
  airportstext?: string | null;
  local?: number;
  localtext?: string | null;
}

export type Quadrant =
  | "Buy"
  | "Negotiate"
  | "Consider"
  | "Evaluate"
  | "Insufficient data";

/**
 * Tilt Map placement (config_version tiltmap-v1) — the active scoring output,
 * replacing the single Tilt Score. x/y are 0-100 plot coords (50 = neutral:
 * x>~60 = above market, y>50 = flexible seller). The Saved board summary carries
 * the plotting fields; the full report response adds recommendation + facts.
 */
export interface TiltMap {
  quadrant: Quadrant;
  x_position: number | null;
  y_position: number | null;
  above_market: boolean | null;
  flexible: boolean | null;
  short_sale_lender_gated?: boolean;
  recommendation?: string;
  ask_to_fve_ratio?: number | null;
  fve?: number | null;
  fve_source?: string | null;
  missing_data?: string[];
}

export interface ScoreResponse {
  score_id: string;
  // The scored property's address — titles the Pro + shared report (#37).
  // place_id + asking_price let a share-link visitor (who never typed the
  // price) render Pro-parity: the map, offer-range base, $/sqft, cost-to-own.
  address: string;
  place_id: string;
  asking_price: number;
  // Geocoded coords, persisted at score time, so the shared report renders the
  // map with ZERO live calls (no billable place_id re-resolution). null on
  // legacy records → map placeholder (#37).
  lat: number | null;
  lon: number | null;
  // The single 0-100 Tilt Score is retired — the Tilt Map is the active model.
  tilt_map: TiltMap | null;
  offer_range: OfferRange;
  factors: FactorCard[];
  confidence: ConfidenceLabel;
  missing_data: string[];
  narrative: string;
  flip_risk: FlipRisk | null;
  closing_cost: ClosingCost;
  // Pro facts (Task 9.7a) — for the extended summary card + client-side
  // offer-range. Nullable when ATTOM lacks them.
  avm_value: number | null;
  avm_12mo_change_pct: number | null;
  avm_confidence: number | null; // ATTOM `scr` 0-100 → shown as AVM certainty %
  flood_zone: string | null;
  recent_permit_count: number | null;
  // Licensed fact not easily found online — owner equity %, surfaced in the
  // paid facts card. From ATTOM /valuation/homeequity. Nullable when absent.
  equity_pct: number | null;
  // Active Listing licensed facts (property-facts Task 5) — from the
  // expandedprofile call. On Pro these replace the free tier's web-search
  // specs so the listing data is sourced from licensed records. original_loan
  // is the ORIGINATION amount (labeled "Original loan", never "current
  // balance"). Every leaf nullable for graceful degradation.
  beds: number | null;
  baths: number | null;
  living_sqft: number | null;
  year_built: number | null;
  lot_size_acres: number | null;
  last_sale_price: number | null;
  last_sale_date: string | null; // ISO 8601
  assessed_value: number | null;
  market_value: number | null;
  annual_tax: number | null;
  owner_occupied: boolean | null;
  // FEMA National Risk Index natural-hazard ratings: {hazard: rating} across
  // all 18 NRI hazards ("Very Low"…"Very High"). Area (census-tract) risk;
  // the card surfaces only the hazards rated above-low. Null if unavailable.
  nri_hazards: Record<string, string> | null;
  // Neighborhood (area) facts from the community call — objective data points.
  // crime_index + air_quality_index are 100 = U.S. average (lower is better).
  crime_index: number | null;
  air_quality_index: number | null;
  owner_occupancy_pct: number | null;
  vacancy_pct: number | null;
  median_commute_min: number | null;
  median_household_income: number | null;
  bachelors_plus_pct: number | null;
  // Licensed location facts (display only). walk_score carries the required
  // Walk Score® attribution (logo_url + backlink). soundscore is HowLoud's
  // noise rating where HIGHER = QUIETER. Null when unavailable / no coverage.
  walk_score: WalkScoreFact | null;
  soundscore: SoundscoreFact | null;
  scored_at: string; // ISO 8601
  share_url?: string;
  chat_history?: ChatTurn[];
  // pro-ai-analyst Task 8 — the narrative now streams separately from the
  // synchronous score. `narrative` is "" here; the client POSTs {score_id} to
  // `narrative_url` (the analyst Function URL) to stream investigation steps +
  // the brief. On a reload, GET /score returns the persisted narrative
  // (non-empty) and the client renders it without re-streaming.
  narrative_url?: string;
  // Entitlement usage after this report (usage-tracking-limits Req 14.1).
  // Omitted if the usage read failed — never blocks the report.
  usage?: UsageStatus;
}

export interface AutocompleteSuggestion {
  address: string;
  place_id: string;
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[];
}

/** Resolve mode of /autocomplete (Task 9.2): place_id -> coords for the map. */
export interface ResolvedPlace {
  place_id: string;
  address: string;
  lat: number;
  lon: number;
  state_code: string | null;
  county_fips: string | null;
}

export interface ResolvePlaceResponse {
  place: ResolvedPlace;
}

/**
 * Free-tier property summary card (Task 9.9a). Sourced from web search +
 * one LLM extraction — NOT ATTOM. Every leaf is nullable; the card renders
 * an em dash for missing fields rather than hiding the row.
 */
export interface PropertySummary {
  listing_status: "active" | "off-market" | "pending" | "unknown" | null;
  last_sale: { price: number | null; date: string | null } | null;
  specs: {
    beds: number | null;
    baths: number | null;
    sqft: number | null;
    year_built: number | null;
    lot_size: string | null;
  } | null;
  value_range: { low: number | null; high: number | null; source: string | null } | null;
  negotiation_room: { label: string; rationale: string } | null;
  /** Short buyer-focused prose summary from the buyer-intelligence prompt. */
  narrative: string | null;
  /**
   * Deterministic confidence in the free-tier data (computed server-side).
   * "low" → the public web was thin; surface the contextual Pro nudge.
   */
  data_confidence: "high" | "medium" | "low";
}

export interface PropertySummaryResponse {
  summary: PropertySummary;
}

/**
 * Entitlement usage (usage-tracking-limits Req 14/15). Embedded in score
 * responses (`ScoreResponse.usage`), returned by GET /usage, and attached to
 * 403/429 error bodies. `tier` reports the paid tier as "pro". Pro users carry
 * `period_resets_at` + `reports_remaining`; free users carry `pro_trial_*`.
 */
export interface UsageStatus {
  tier: "pro" | "free";
  reports_used: number;
  reports_limit: number;
  // Pro
  period_resets_at?: string | null;
  reports_remaining?: number;
  properties_scored?: string[];
  subscription_ended?: boolean;
  // Free
  pro_trial_remaining?: number;
  pro_trial_spent_on?: string | null;
  // score_id of the report the free user unlocked with their trial — lets the
  // free UI re-open the full Pro report for that property (permanent unlock).
  pro_trial_score_id?: string | null;
  // Present when the trial is unavailable for a reason other than this account
  // spending it — e.g. "email_already_used" (the email's one grant belongs to
  // another/deleted account, Req 7.2). The UI must not offer "Try Pro".
  trial_unavailable_reason?: string;
}

/**
 * Profile preferences (chat-personalization Req 10). Returned by GET /user/profile
 * and sent (partially) via PUT /user/profile. All fields optional — a user who
 * hasn't saved preferences yet gets nulls.
 */
export interface ProfilePreferences {
  display_name: string | null;
  search_requirements: string | null;
  target_areas: string[] | null;
}

/** PUT /user/profile request body. */
export interface PutProfileRequest {
  display_name?: string | null;
  search_requirements?: string | null;
  target_areas?: string[] | null;
}

/** PUT /user/profile response. */
export interface PutProfileResponse {
  updated: boolean;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
  };
  usage?: UsageStatus;
  upgrade_url?: string;
  required_action?: string;
  trial_spent?: boolean;
  period_resets_at?: string | null;
  awaiting_renewal?: boolean;
  retry_after?: number;
}

export class ApiError extends Error {
  status: number;
  code: string;
  /** Entitlement usage attached to 403/429 denials (Req 14.2). */
  usage?: UsageStatus;
  /** Remaining fields from a structured entitlement error (upgrade_url, etc.). */
  extra?: Omit<ApiErrorBody, "error">;
  constructor(
    status: number,
    code: string,
    message: string,
    extra?: Omit<ApiErrorBody, "error">,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.usage = extra?.usage;
    this.extra = extra;
  }
}
